import { Package } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import type { StockTransformationLine } from "@artisancode/api-types";

interface Props {
  lines: StockTransformationLine[];
  totalCost: number;
}

const rp = formatCurrency;
const rpRounded = formatCurrencyRounded;

export function ProductionInputLines({ lines, totalCost }: Props) {
  const isMobile = useIsMobile();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span>Bahan Baku yang Digunakan (Input)</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {lines.length} jenis bahan
        </span>
      </div>

      {isMobile ? (
        <div className="space-y-2">
          {lines.map((l) => (
            <div
              key={l.id}
              className="space-y-2 rounded-lg border bg-card p-3 text-xs"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-foreground">
                    {l.itemName}
                  </div>
                  {l.itemSku && (
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {l.itemSku}
                    </div>
                  )}
                </div>
                <div className="text-right font-mono font-bold text-foreground">
                  {l.qty.toLocaleString("id-ID")} {l.uomName}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-border/40 pt-1.5 text-[11px] text-muted-foreground">
                <span>
                  Biaya: {rp(l.unitCost)} / {l.uomName}
                </span>
                <span className="font-mono text-xs font-semibold text-foreground">
                  Subtotal: {rpRounded(l.qty * l.unitCost)}
                </span>
              </div>

              {l.note && (
                <p className="rounded bg-muted/20 p-1.5 text-[11px] italic text-muted-foreground">
                  {l.note}
                </p>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between rounded-lg border bg-muted/20 p-3 text-xs font-semibold">
            <span>Total Biaya Bahan Baku:</span>
            <span className="font-mono text-sm text-foreground">
              {rpRounded(totalCost)}
            </span>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Bahan Baku</TableHead>
                <TableHead className="text-right">Jumlah Terpakai</TableHead>
                <TableHead className="text-right">Biaya Satuan</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead>Keterangan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    <div className="font-medium">{l.itemName}</div>
                    {l.itemSku && (
                      <div className="font-mono text-xs text-muted-foreground">
                        {l.itemSku}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {l.qty.toLocaleString("id-ID")} {l.uomName}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {rp(l.unitCost)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {rpRounded(l.qty * l.unitCost)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {l.note || "-"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/20 font-semibold">
                <TableCell colSpan={3} className="text-right">
                  Total Biaya Bahan Baku:
                </TableCell>
                <TableCell className="text-right font-mono text-base">
                  {rpRounded(totalCost)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
